import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { ConversationOutcome, Draft } from '@anchor-comms/shared';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { formatDate } from '@/lib/utils';
import {
  useAddInboundMessage,
  useConversation,
  useLogSentMessage,
  useUpdateConversation,
} from './queries';
import { streamGenerate } from './sse';

const OUTCOMES: ConversationOutcome[] = ['won', 'lost', 'in_progress', 'abandoned'];

export function ConversationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const { data, isLoading, error } = useConversation(id);
  const addInbound = useAddInboundMessage(id ?? '');
  const logSent = useLogSentMessage(id ?? '');
  const updateConv = useUpdateConversation(id ?? '');

  const [draftText, setDraftText] = useState('');
  const [rationale, setRationale] = useState<string | null>(null);
  const [sourceDraftId, setSourceDraftId] = useState<string | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamError, setStreamError] = useState<string | null>(null);
  const [repContext, setRepContext] = useState('');
  const [showRationale, setShowRationale] = useState(false);
  const [inboundContent, setInboundContent] = useState('');
  const [noteContent, setNoteContent] = useState('');
  const abortRef = useRef<{ abort: () => void } | null>(null);

  // Latest draft on load
  const latestDraft: Draft | undefined = data?.drafts?.[0];

  useEffect(() => {
    if (latestDraft && !isStreaming && draftText === '' && !sourceDraftId) {
      setDraftText(latestDraft.content);
      setRationale(latestDraft.rationale);
      setSourceDraftId(latestDraft.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestDraft?.id]);

  const onGenerate = () => {
    if (!id) return;
    setDraftText('');
    setRationale(null);
    setSourceDraftId(null);
    setStreamError(null);
    setIsStreaming(true);
    abortRef.current = streamGenerate(id, repContext.trim() || null, {
      onToken: (delta) => setDraftText((prev) => prev + delta),
      onComplete: (draft) => {
        setIsStreaming(false);
        setSourceDraftId(draft.id);
        setRationale(draft.rationale);
        // Replace incremental text with the final canonical content (handles rare
        // streaming/JSON-escape edge cases).
        setDraftText(draft.content);
        void qc.invalidateQueries({ queryKey: ['conversation', id] });
      },
      onError: (code, message) => {
        setIsStreaming(false);
        setStreamError(`${code}: ${message}`);
      },
    });
  };

  const onCancelStream = () => {
    abortRef.current?.abort();
    setIsStreaming(false);
  };

  const onCopy = async () => {
    await navigator.clipboard.writeText(draftText);
  };

  const onMarkSent = () => {
    if (!id || !draftText.trim()) return;
    logSent.mutate(
      { content: draftText, sourceDraftId: sourceDraftId ?? undefined },
      {
        onSuccess: () => {
          setDraftText('');
          setRationale(null);
          setSourceDraftId(null);
          setRepContext('');
        },
      },
    );
  };

  const onAddInbound = () => {
    if (!id || !inboundContent.trim()) return;
    addInbound.mutate(
      { role: 'customer', content: inboundContent.trim() },
      { onSuccess: () => setInboundContent('') },
    );
  };

  const onAddNote = () => {
    if (!id || !noteContent.trim()) return;
    addInbound.mutate(
      { role: 'rep_note', content: noteContent.trim() },
      { onSuccess: () => setNoteContent('') },
    );
  };

  const usedDraftIds = useMemo(
    () => new Set(data?.messages.map((m) => m.sourceDraftId).filter(Boolean) ?? []),
    [data?.messages],
  );

  if (isLoading) return <p className="text-muted-foreground">Loading…</p>;
  if (error) return <p className="text-destructive">{(error as Error).message}</p>;
  if (!data) return null;

  const isClosed = data.status === 'closed';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{data.customer.name}</h1>
          <p className="text-sm text-muted-foreground">
            {data.topic.name} · Rep: {data.rep.name}
            {data.customer.company && ` · ${data.customer.company}`}
            {data.customer.bookingReference && ` · Booking ${data.customer.bookingReference}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={isClosed ? 'secondary' : 'default'}>{data.status}</Badge>
          {data.outcome && <Badge variant="outline">{data.outcome.replace('_', ' ')}</Badge>}
        </div>
      </div>

      {/* Thread */}
      <Card>
        <CardHeader>
          <CardTitle>Thread</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {data.messages.length === 0 && (
            <p className="text-muted-foreground text-sm">No messages yet. Add an inbound message or generate a draft.</p>
          )}
          {data.messages.map((m) => (
            <div key={m.id} className="flex gap-3">
              <div className="w-20 shrink-0 text-xs text-muted-foreground pt-1">
                {m.role === 'customer' ? 'Customer' : m.role === 'rep_sent' ? 'Rep sent' : 'Note'}
              </div>
              <div className="flex-1 space-y-1">
                <div className="rounded-md bg-muted px-3 py-2 text-sm whitespace-pre-wrap">{m.content}</div>
                <div className="text-xs text-muted-foreground flex gap-2">
                  <span>{formatDate(m.sentAt ?? m.createdAt)}</span>
                  {m.wasEdited && <span>· edited from draft</span>}
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Draft area */}
      {!isClosed && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Draft</CardTitle>
            <div className="flex items-center gap-2">
              {!isStreaming && (
                <Button onClick={onGenerate} disabled={isStreaming}>
                  {draftText ? 'Regenerate' : 'Generate draft'}
                </Button>
              )}
              {isStreaming && (
                <Button variant="outline" onClick={onCancelStream}>
                  Cancel
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="repContext">Context for this draft (optional)</Label>
              <Textarea
                id="repContext"
                rows={2}
                placeholder="e.g. customer just confirmed by phone they want to pay this week"
                value={repContext}
                onChange={(e) => setRepContext(e.target.value)}
              />
            </div>
            <Textarea
              rows={10}
              value={draftText}
              onChange={(e) => setDraftText(e.target.value)}
              placeholder="Generate a draft or write your own message here."
            />
            {streamError && <p className="text-sm text-destructive">{streamError}</p>}
            {rationale && (
              <div className="text-sm">
                <button
                  type="button"
                  onClick={() => setShowRationale((v) => !v)}
                  className="text-xs text-muted-foreground hover:text-foreground"
                >
                  {showRationale ? 'Hide rationale' : 'Why this approach?'}
                </button>
                {showRationale && (
                  <p className="mt-2 text-muted-foreground italic">{rationale}</p>
                )}
              </div>
            )}
            <div className="flex flex-wrap gap-2 justify-end">
              <Button variant="outline" onClick={onCopy} disabled={!draftText}>
                Copy
              </Button>
              <Button onClick={onMarkSent} disabled={!draftText.trim() || logSent.isPending}>
                {logSent.isPending ? 'Logging…' : 'Mark sent'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Drafts history (helpful diagnostic for managers) */}
      {data.drafts.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Previous drafts</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {data.drafts.map((d) => (
              <div key={d.id} className="text-sm rounded border p-3 space-y-1">
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>{formatDate(d.createdAt)}</span>
                  <span>
                    {d.model} · {d.promptVersion} · tokens {d.tokenUsage.total}
                    {usedDraftIds.has(d.id) ? ' · used' : ''}
                  </span>
                </div>
                <div className="whitespace-pre-wrap">{d.content}</div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Add customer reply / note */}
      {!isClosed && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardHeader>
              <CardTitle>Add customer reply</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Textarea
                rows={4}
                placeholder="Paste the customer's reply"
                value={inboundContent}
                onChange={(e) => setInboundContent(e.target.value)}
              />
              <div className="flex justify-end">
                <Button onClick={onAddInbound} disabled={!inboundContent.trim() || addInbound.isPending}>
                  Add reply
                </Button>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Add note</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Textarea
                rows={4}
                placeholder="e.g. called the customer, left voicemail"
                value={noteContent}
                onChange={(e) => setNoteContent(e.target.value)}
              />
              <div className="flex justify-end">
                <Button onClick={onAddNote} disabled={!noteContent.trim() || addInbound.isPending}>
                  Add note
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Close / outcome */}
      <Card>
        <CardHeader>
          <CardTitle>Status</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-sm text-muted-foreground">Outcome:</span>
            {OUTCOMES.map((o) => (
              <Button
                key={o}
                size="sm"
                variant={data.outcome === o ? 'default' : 'outline'}
                onClick={() => updateConv.mutate({ outcome: o })}
              >
                {o.replace('_', ' ')}
              </Button>
            ))}
            <Button size="sm" variant="ghost" onClick={() => updateConv.mutate({ outcome: null })}>
              Clear
            </Button>
          </div>
          <div className="space-y-2">
            <Label htmlFor="outcomeNotes">Notes</Label>
            <Textarea
              id="outcomeNotes"
              rows={2}
              defaultValue={data.outcomeNotes ?? ''}
              onBlur={(e) => {
                if ((e.target.value || null) !== (data.outcomeNotes ?? null)) {
                  updateConv.mutate({ outcomeNotes: e.target.value || null });
                }
              }}
              placeholder="Optional notes about the outcome"
            />
          </div>
          <div className="flex justify-end">
            {isClosed ? (
              <Button variant="outline" onClick={() => updateConv.mutate({ status: 'active' })}>
                Reopen
              </Button>
            ) : (
              <Button variant="destructive" onClick={() => updateConv.mutate({ status: 'closed' })}>
                Close conversation
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
